import React from "react";
import { Box, Button, Grid, IconButton, Popover, Stack, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { Dayjs } from "dayjs";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const monthSelectorButtonSx = {
  border: "1.5px solid",
  borderColor: "primary.main",
  borderRadius: 1,
  color: "primary.main",
  minWidth: 36,
  height: 36,
  p: 0,
  "&:hover": { backgroundColor: "rgba(255, 158, 0, 0.1)", borderColor: "primary.main" },
  "&.Mui-disabled": { borderColor: "rgba(255, 158, 0, 0.3)", color: "rgba(255, 158, 0, 0.3)" },
};

export interface ScheduledReportsMonthSelectorProps {
  selectedMonth: Dayjs;
  hasNoReports: boolean;
  monthPickerAnchor: HTMLElement | null;
  pickerYear: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
  canGoToPreviousYear: boolean;
  canGoToNextYear: boolean;
  hasReport: (monthIndex: number, year: number) => boolean;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onMonthPickerOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onMonthPickerClose: () => void;
  onMonthSelect: (monthIndex: number) => void;
  onPickerYearChange: (delta: number) => void;
}

export const ScheduledReportsMonthSelector: React.FC<ScheduledReportsMonthSelectorProps> = ({
  selectedMonth,
  hasNoReports,
  monthPickerAnchor,
  pickerYear,
  canGoPrevious,
  canGoNext,
  canGoToPreviousYear,
  canGoToNextYear,
  hasReport,
  onPreviousMonth,
  onNextMonth,
  onMonthPickerOpen,
  onMonthPickerClose,
  onMonthSelect,
  onPickerYearChange,
}) => {
  const monthPickerOpen = Boolean(monthPickerAnchor);

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: -2 }}>
        <IconButton onClick={onPreviousMonth} disabled={!canGoPrevious || hasNoReports} sx={monthSelectorButtonSx}>
          <ChevronLeftIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box
          onClick={hasNoReports ? undefined : onMonthPickerOpen}
          sx={{
            border: "1.5px solid",
            borderColor: hasNoReports ? "grey.600" : "primary.main",
            borderRadius: 1,
            px: 2,
            height: 36,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: hasNoReports ? "default" : "pointer",
            opacity: hasNoReports ? 0.5 : 1,
            "&:hover": { backgroundColor: hasNoReports ? "transparent" : "rgba(255, 158, 0, 0.1)" },
          }}
        >
          <CalendarTodayOutlinedIcon sx={{ color: hasNoReports ? "grey.600" : "primary.main", fontSize: 16 }} />
          <Typography sx={{ color: hasNoReports ? "grey.600" : "primary.main", fontWeight: 600, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {hasNoReports ? "No Data" : selectedMonth.format("MMMM YYYY")}
          </Typography>
        </Box>
        <IconButton onClick={onNextMonth} disabled={!canGoNext || hasNoReports} sx={monthSelectorButtonSx}>
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      <Popover
        open={monthPickerOpen}
        anchorEl={monthPickerAnchor}
        onClose={onMonthPickerClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            backgroundColor: "#1E1E1E",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 2,
            mt: 1,
            width: 200,
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, pb: 1, borderBottom: "1px solid rgba(255,255,255,0.1)", px: 1 }}>
            <Button
              onClick={() => onPickerYearChange(-1)}
              disabled={!canGoToPreviousYear}
              sx={{ color: "white", minWidth: 28, p: 0.25, "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" } }}
            >
              <ChevronLeftIcon fontSize="small" />
            </Button>
            <Typography sx={{ color: "white", fontWeight: 500, fontSize: 14, px: 2 }}>{pickerYear}</Typography>
            <Button
              onClick={() => onPickerYearChange(1)}
              disabled={!canGoToNextYear}
              sx={{ color: "white", minWidth: 28, p: 0.25, "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" } }}
            >
              <ChevronRightIcon fontSize="small" />
            </Button>
          </Stack>
          <Grid container spacing={0.5}>
            {MONTH_NAMES.map((month, index) => {
              const isAvailable = hasReport(index, pickerYear);
              const isSelected = selectedMonth.month() === index && selectedMonth.year() === pickerYear;
              return (
                <Grid item xs={4} key={month}>
                  <Button
                    fullWidth
                    onClick={() => onMonthSelect(index)}
                    disabled={!isAvailable}
                    sx={{
                      color: isSelected ? "primary.main" : !isAvailable ? "rgba(255,255,255,0.3)" : "white",
                      fontWeight: isSelected ? 600 : 400,
                      fontSize: 13,
                      py: 0.75,
                      minWidth: 0,
                      "&:hover": { backgroundColor: isAvailable ? "rgba(255,255,255,0.1)" : "transparent" },
                      "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
                    }}
                  >
                    {month}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Popover>
    </>
  );
};
